import { formatUserFacingError } from '@/lib/errors'
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  GripVertical,
  Pencil,
  Trash2,
  FileText,
  List,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { MultiButton, MultiButtonGroup } from '@/components/ui/multi-button'
import { AdminListPage } from '../components/AdminListPage'
import { useAdminApplications } from '../hooks/useAdminApplications'
import { adminApplicationsService } from '../services/admin.applications.service'
import { adminSubmissionsService } from '../services/admin.submissions.service'
import type { ApplicationDocument } from '@/types'

type Application = ApplicationDocument & { id: string }

const STATUS_LABELS: Record<ApplicationDocument['status'], string> = {
  draft: 'Draft',
  open: 'Open',
  closed: 'Closed',
}

const STATUS_VARIANTS: Record<
  ApplicationDocument['status'],
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  draft: 'secondary',
  open: 'default',
  closed: 'destructive',
}

interface SortableApplicationRowProps {
  app: Application
  submissionCount: number
  deletingId: string | null
  togglingId: string | null
  onEdit: (id: string) => void
  onEditForm: (id: string) => void
  onViewSubmissions: (id: string) => void
  onTogglePublished: (app: Application) => void
  onDelete: (app: Application) => void
}

function SortableApplicationRow({
  app,
  submissionCount,
  deletingId,
  togglingId,
  onEdit,
  onEditForm,
  onViewSubmissions,
  onTogglePublished,
  onDelete,
}: SortableApplicationRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: app.id,
  })

  return (
    <TableRow
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      <TableCell className="w-8 pr-0">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing touch-none text-muted-foreground hover:text-foreground"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      </TableCell>
      <TableCell className="font-medium">{app.title}</TableCell>
      <TableCell className="font-mono text-sm text-muted-foreground">{app.slug}</TableCell>
      <TableCell>
        <Badge variant={STATUS_VARIANTS[app.status]}>{STATUS_LABELS[app.status]}</Badge>
      </TableCell>
      <TableCell>
        <Badge variant={app.isPublished ? 'default' : 'secondary'}>
          {app.isPublished ? 'Published' : 'Hidden'}
        </Badge>
      </TableCell>
      <TableCell className="text-sm">
        <button
          type="button"
          onClick={() => onViewSubmissions(app.id)}
          className="font-medium text-foreground hover:underline"
        >
          {submissionCount}
        </button>
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {app.deadline
          ? new Date(
              (app.deadline as unknown as { toDate(): Date }).toDate?.() ?? app.deadline
            ).toLocaleDateString()
          : '—'}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end">
          <MultiButton
            variant="outline"
            size="sm"
            items={[
              {
                id: 'edit',
                icon: Pencil,
                label: 'Edit Details',
                onClick: () => onEdit(app.id),
              },
              {
                id: 'form',
                icon: FileText,
                label: 'Edit Form',
                onClick: () => onEditForm(app.id),
              },
              {
                id: 'submissions',
                icon: List,
                label: 'View Submissions',
                onClick: () => onViewSubmissions(app.id),
              },
              {
                id: 'toggle',
                icon: app.isPublished ? ToggleRight : ToggleLeft,
                label: app.isPublished ? 'Hide from listing' : 'Show in listing',
                onClick: () => onTogglePublished(app),
                disabled: togglingId === app.id,
              },
              {
                id: 'delete',
                icon: Trash2,
                label: 'Delete',
                onClick: () => onDelete(app),
                disabled: deletingId === app.id,
                className: 'text-destructive hover:text-destructive',
              },
            ]}
          />
        </div>
      </TableCell>
    </TableRow>
  )
}

export function AdminApplicationsPage() {
  const navigate = useNavigate()
  const { applications, loading, error, deleteApplication, updatePublished, refresh } =
    useAdminApplications()
  const [orderedApps, setOrderedApps] = useState<Application[]>([])
  const [savingOrder, setSavingOrder] = useState(false)
  const [orderError, setOrderError] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Application | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [togglingId, setTogglingId] = useState<string | null>(null)
  const [submissionCounts, setSubmissionCounts] = useState<Record<string, number>>({})

  useEffect(() => {
    setOrderedApps([...applications].sort((a, b) => a.order - b.order))
  }, [applications])

  useEffect(() => {
    let cancelled = false
    adminSubmissionsService
      .getCountsByApplicationId()
      .then((counts) => {
        if (!cancelled) setSubmissionCounts(counts)
      })
      .catch(() => {
        // non-fatal: counts simply remain at 0
      })
    return () => {
      cancelled = true
    }
  }, [applications])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = orderedApps.findIndex((a) => a.id === active.id)
    const newIndex = orderedApps.findIndex((a) => a.id === over.id)
    const reordered = arrayMove(orderedApps, oldIndex, newIndex)

    setOrderedApps(reordered)
    setSavingOrder(true)
    setOrderError(null)

    try {
      await Promise.all(
        reordered.map((app, index) =>
          adminApplicationsService.update(app.id, { order: index + 1 }),
        ),
      )
      refresh()
    } catch (err) {
      setOrderError(formatUserFacingError(err, 'Failed to save order'))
    } finally {
      setSavingOrder(false)
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return
    setDeletingId(deleteTarget.id)
    try {
      await deleteApplication(deleteTarget.id)
    } finally {
      setDeletingId(null)
      setDeleteTarget(null)
      setDialogOpen(false)
    }
  }

  async function handleTogglePublished(app: Application) {
    setTogglingId(app.id)
    try {
      await updatePublished(app.id, !app.isPublished)
    } finally {
      setTogglingId(null)
    }
  }

  function openDeleteDialog(app: Application) {
    setDeleteTarget(app)
    setDialogOpen(true)
  }

  return (
    <AdminListPage
      title="Applications"
      action={
        <Button asChild>
          <Link to="/admin/applications/new">New Application</Link>
        </Button>
      }
      error={error ?? orderError}
      loading={loading}
      skeletonRows={4}
      dialog={
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger className="hidden" />
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Application</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              Are you sure you want to delete{' '}
              <span className="font-semibold text-foreground">{deleteTarget?.title}</span>? This
              will not delete existing submissions, but the form will be inaccessible.
            </p>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setDialogOpen(false)
                  setDeleteTarget(null)
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => void handleDelete()}
                disabled={deletingId !== null}
              >
                {deletingId !== null ? 'Deleting…' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      }
    >
      {savingOrder && (
        <p className="text-xs text-muted-foreground -mt-2 mb-2">Saving order…</p>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={(e) => void handleDragEnd(e)}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>Title</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Visible</TableHead>
              <TableHead>Submissions</TableHead>
              <TableHead>Deadline</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <MultiButtonGroup>
            <SortableContext
              items={orderedApps.map((a) => a.id)}
              strategy={verticalListSortingStrategy}
            >
              <TableBody>
                {orderedApps.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-8 text-center text-muted-foreground">
                      No applications yet. Create one to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  orderedApps.map((app) => (
                    <SortableApplicationRow
                      key={app.id}
                      app={app}
                      submissionCount={submissionCounts[app.id] ?? 0}
                      deletingId={deletingId}
                      togglingId={togglingId}
                      onEdit={(id) => navigate(`/admin/applications/${id}/edit`)}
                      onEditForm={(id) => navigate(`/admin/applications/${id}/form`)}
                      onViewSubmissions={(id) =>
                        navigate(`/admin/applications/${id}/submissions`)
                      }
                      onTogglePublished={(a) => void handleTogglePublished(a)}
                      onDelete={openDeleteDialog}
                    />
                  ))
                )}
              </TableBody>
            </SortableContext>
          </MultiButtonGroup>
        </Table>
      </DndContext>
    </AdminListPage>
  )
}
